// src/pages/Documentation/components/DocsSidebar.jsx
import { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SidebarSearch } from './SidebarSearch';
import './DocsSidebar.scss';
import { ThemeContext } from '../../ThemeContext';

export const DocsSidebar = ({
  structure,
  activeSection,
  onSectionChange,
  initialExpandedGroups = {},
}) => {
  const { theme } = useContext(ThemeContext);
  const [expandedGroups, setExpandedGroups] = useState(initialExpandedGroups);

  // Only update expandedGroups on initial mount or when structure changes significantly
  useEffect(() => {
    // Only merge initial expanded groups if they don't exist in current state
    setExpandedGroups((prev) => {
      const newState = { ...prev };
      let hasChanges = false;

      Object.keys(initialExpandedGroups).forEach((key) => {
        if (!(key in prev)) {
          newState[key] = initialExpandedGroups[key];
          hasChanges = true;
        }
      });

      return hasChanges ? newState : prev;
    });
  }, [structure]); // Changed dependency from initialExpandedGroups to structure

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleSectionClick = (section) => {
    // Prevent event bubbling that might trigger group toggles
    onSectionChange(section);
  };

  const renderSidebarItem = (item, nestingLevel = 0) => {
    switch (item.type) {
      case 'link':
        return (
          <motion.li
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="sidebar-item"
          >
            <button
              className={`sidebar-link ${
                activeSection?.id === item.id ? 'active' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                handleSectionClick(item);
              }}
            >
              <i className="bi bi-file-text me-2"></i>
              {item.title}
            </button>
          </motion.li>
        );

      case 'divider':
        return (
          <div key={item.id} className="sidebar-divider">
            <span>{item.title}</span>
          </div>
        );

      case 'group':
        return (
          <div
            key={item.id}
            className={`sidebar-group ${
              nestingLevel > 0 ? 'nested-group' : ''
            }`}
          >
            <button
              className={`group-header ${
                nestingLevel > 0 ? 'nested-header' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                toggleGroup(item.id);
              }}
            >
              <i
                className={`bi bi-chevron-${
                  expandedGroups[item.id] ? 'down' : 'right'
                } me-2`}
              ></i>
              <span>{item.title}</span>
            </button>
            <AnimatePresence>
              {expandedGroups[item.id] && (
                <motion.ul
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`sidebar-nav nested ${
                    nestingLevel > 0 ? 'double-nested' : ''
                  }`}
                >
                  {item.sections.map((section) => {
                    // Check if section is a nested group or a regular section
                    if (section.type === 'group') {
                      return renderSidebarItem(section, nestingLevel + 1);
                    } else {
                      return (
                        <motion.li
                          key={section.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="sidebar-item"
                        >
                          <button
                            className={`sidebar-link ${
                              activeSection?.id === section.id ? 'active' : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent event bubbling
                              handleSectionClick(section);
                            }}
                          >
                            <i className="bi bi-file-earmark-text me-2"></i>
                            {section.title}
                          </button>
                        </motion.li>
                      );
                    }
                  })}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <nav
      className={`docs-sidebar docs-sidebar-sticky ${
        theme === 'dark' ? 'dark-theme' : ''
      }`}
    >
      <div className="docs-sidebar-content">
        <div className="d-md-none p-3 border-bottom">
          <h5 className="m-0">Documentation</h5>
        </div>
        <SidebarSearch
          structure={structure}
          onSectionChange={onSectionChange}
        />
        <div className="sidebar-nav-wrapper">
          <div className="sidebar-nav">
            {structure.map((item) => renderSidebarItem(item, 0))}
          </div>
        </div>
      </div>
    </nav>
  );
};
